import { createSelector } from '@reduxjs/toolkit';
import { applyPendingChanges, buildBaselineMap, hasSaturationChanges } from 'utils/saturationChanges';

export const selectDeskSlice = (state) => state.desks;

export const selectAllDesks = createSelector([selectDeskSlice], (desksSlice) => desksSlice.allDesks);

export const selectHasAvailableDesks = createSelector([selectAllDesks], (allDesks) => !!allDesks.length);

export const selectCurrentDesk = createSelector([selectDeskSlice], (desksSlice) => desksSlice.currentDesk);

export const selectFetchingDesksInProgress = createSelector([selectDeskSlice], (desksSlice) => desksSlice.fetchingDesksInProgress);

export const selectFetchingDesksError = createSelector([selectDeskSlice], (desksSlice) => desksSlice.fetchingDesksError);

export const selectMaxDeskTransactions = createSelector([selectDeskSlice], (deskSlice) => deskSlice.maxDeskTransactions);

export const selectIsActiveDeskConfirmed = createSelector([selectDeskSlice], (deskSlice) => deskSlice.isActiveDeskConfirmed);

export const selectExpectedFinishedTransactionMillis = createSelector([selectDeskSlice], (deskSlice) => deskSlice.expectedFinishedTransactionMillis);

export const selectCircleCheckRotationIntervalMillis = createSelector([selectDeskSlice], (deskSlice) => deskSlice.circleCheckRotationIntervalMillis);

export const selectVideoWallCameraWarningLimit = createSelector([selectDeskSlice], (deskSlice) => deskSlice.videoWallCameraWarningLimit);

export const selectTotalStreamingCameraWarningLimit = createSelector([selectDeskSlice], (deskSlice) => deskSlice.totalStreamingCameraWarningLimit);

export const selectDeskWarnings = createSelector([selectDeskSlice], (deskslice) => deskslice.deskWarnings);

export const selectIsDeskLoading = createSelector([selectDeskSlice], (deskslice) => deskslice.isDeskLoading);

export const selectPauseRequestSent = createSelector([selectDeskSlice], (desksSlice) => desksSlice.pauseRequestSent);

export const selectAllDeskLocations = createSelector([selectDeskSlice], (desksSlice) => desksSlice.allDeskLocations);

export const selectSavedDesks = createSelector([selectDeskSlice], (desksSlice) => desksSlice.savedDesk);

export const selectPendingSaturationChanges = createSelector([selectDeskSlice], (desksSlice) => desksSlice.pendingSaturationChanges);

export const selectSavingSaturationInProgress = createSelector([selectDeskSlice], (desksSlice) => desksSlice.savingSaturationInProgress);

export const selectHasSaturationChanges = createSelector([selectPendingSaturationChanges], hasSaturationChanges);

// Pending changes survive a filter change, so the Save button shows how many there are even when
// none of them is on screen.
export const selectSaturationChangeCount = createSelector(
    [selectPendingSaturationChanges],
    (pendingChanges) => Object.keys(pendingChanges || {}).length
);

export const selectIsSaveSaturationDisabled = createSelector(
    [selectHasSaturationChanges, selectSavingSaturationInProgress],
    (hasChanges, savingInProgress) => !hasChanges || savingInProgress
);

export const selectSaturationBaseline = createSelector([selectSavedDesks, selectAllDeskLocations], buildBaselineMap);

/**
 * The matrix as the user currently sees it: persisted state with the unsaved changes on top.
 */
export const selectEffectiveSaturationMappings = createSelector([selectSaturationBaseline, selectPendingSaturationChanges], applyPendingChanges);

export const selectNotFoundFilteredDeskMessage = createSelector([selectDeskSlice], (desksSlice) => desksSlice.notFoundFilteredDeskMessage);

export const selectIsFetchingSaturationInfo = createSelector([selectDeskSlice], (desksSlice) => desksSlice.isFetchingSaturationInfo);

export const selectDivisions = createSelector([selectDeskSlice], (desksSlice) => desksSlice.allDivisions);

export const selectFilterValues = createSelector([selectDeskSlice], (desksSlice) => desksSlice.filterValues);

export const selectFetchingDesksFinished = createSelector([selectDeskSlice], (desksSlice) => desksSlice.fetchingDesksFinished);

export const selectShowSkeleton = createSelector([selectDeskSlice], (desksSlice) => desksSlice.showSkeleton);
